import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDoc,
  doc,
  Timestamp,
  FieldValue
} from 'firebase/firestore';
import { db } from '../firebase';
import { Customer, CustomerChangeHistory, CustomerWithFieldValues } from '../../types/customer';

// Remove the local type definition since we're importing it now
// type CustomerWithFieldValues = Omit<Customer, 'createdAt' | 'updatedAt'> & {
//   createdAt?: Date | Timestamp | FieldValue;
//   updatedAt?: Date | Timestamp | FieldValue;
// };

const COLLECTION = 'customer_history';

/**
 * Track changes to customer data
 */
export const customerHistoryService = {
  /**
   * Record a change to customer data
   * @param customerId The ID of the customer that was changed
   * @param oldData Previous customer data (can be partial or full)
   * @param newData New customer data (can be partial or full)
   * @param userId ID of the user who made the change
   * @param changeType Type of change (create, update, delete)
   * @returns ID of the recorded history entry
   */
  async recordChange(
    customerId: string,
    oldData: Partial<CustomerWithFieldValues> | null,
    newData: Partial<CustomerWithFieldValues> | null,
    userId: string,
    changeType: 'create' | 'update' | 'delete'
  ): Promise<string> {
    // Calculate what fields changed
    const changedFields: { [key: string]: { oldValue: any; newValue: any } } = {};
    
    if (changeType === 'create') {
      // For creation, record all fields as "changed" with null as oldValue
      if (newData) {
        Object.keys(newData).forEach(key => {
          if (key !== 'id' && key !== 'createdAt' && key !== 'updatedAt') {
            const newValue = newData[key as keyof Customer];
            changedFields[key] = {
              oldValue: null,
              newValue: newValue === undefined ? null : newValue
            };
          }
        });
      }
    } else if (changeType === 'update' && oldData && newData) {
      // For update, find all fields that have changed
      const allKeys = new Set([
        ...Object.keys(oldData), 
        ...Object.keys(newData)
      ]);
      
      allKeys.forEach(key => {
        // Skip metadata fields
        if (key === 'id' || key === 'createdAt' || key === 'updatedAt') return;
        
        const oldValue = oldData[key as keyof Customer];
        const newValue = newData[key as keyof Customer];
        
        // Only record if the values are different
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changedFields[key] = {
            oldValue: oldValue === undefined ? null : oldValue,
            newValue: newValue === undefined ? null : newValue
          };
        }
      });
    } else if (changeType === 'delete' && oldData) {
      // For deletion, record all existing fields as changed with null as newValue
      Object.keys(oldData).forEach(key => {
        if (key !== 'id' && key !== 'createdAt' && key !== 'updatedAt') {
          const oldValue = oldData[key as keyof Customer];
          changedFields[key] = {
            oldValue: oldValue === undefined ? null : oldValue,
            newValue: null
          };
        }
      });
    }
    
    // Don't create a history entry if nothing changed
    if (Object.keys(changedFields).length === 0) {
      console.log('No fields changed, skipping history entry');
      return '';
    }
    
    // Create the history record
    const historyEntry: Omit<CustomerChangeHistory, 'id'> = {
      customerId,
      timestamp: serverTimestamp() as Timestamp,
      userId,
      changedFields,
      changeType,
      ...(changeType === 'delete' && oldData ? { snapshot: oldData } : {}),
      ...(changeType !== 'delete' && newData ? { snapshot: newData } : {})
    };
    
    const docRef = await addDoc(collection(db, COLLECTION), historyEntry);
    return docRef.id;
  },
  
  /**
   * Get the change history for a specific customer
   */
  async getCustomerHistory(customerId: string): Promise<CustomerChangeHistory[]> {
    console.log('Fetching customer history from Firestore:', customerId);
    
    try {
      // Construct a query that will work correctly with Firestore indices
    const q = query(
      collection(db, COLLECTION),
      where('customerId', '==', customerId),
      orderBy('timestamp', 'desc')
    );
    
      // Simply get the data - skip the connection test that might cause redundant requests
    const snapshot = await getDocs(q);
      console.log('Found', snapshot.size, 'history records for customer', customerId);
      
      if (snapshot.empty) {
        console.log('No history records found for customer:', customerId);
        return [];
      }
      
      const results = snapshot.docs.map(doc => {
        const data = doc.data();
        // Check if timestamp exists
        if (!data.timestamp) {
          console.warn('Missing timestamp in history record', doc.id);
        }
        
        return {
      id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(),
          changedFields: data.changedFields || {}
        };
      }) as CustomerChangeHistory[];
      
      console.log('Processed history records:', results.length);
      return results;
    } catch (error) {
      console.error('Error fetching customer history:', error);
      throw error;
    }
  },
  
  /**
   * Get a specific history entry by ID
   */
  async getHistoryEntry(id: string): Promise<CustomerChangeHistory | null> {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data(),
      timestamp: docSnap.data().timestamp?.toDate() || new Date()
    } as CustomerChangeHistory;
  }
}; 